/**
 * BrandMasthead — shared brand masthead atom.
 *
 * Renders the iTow brand lockup used at the top of marketing-style pages
 * (login, forgot-password, brand-only home placeholders). The caller owns the
 * surrounding centering Box / Paper layout — this component renders ONLY the
 * lockup itself.
 *
 * The lockup is the real logo artwork (see <BrandLogo>), which already contains
 * the "iTow" wordmark. The brand name is therefore NOT repeated as visible
 * text — it is rendered as a visually-hidden heading so the page keeps a real
 * <h1> for screen readers and document outline without showing the word twice.
 * Pass `tagline` for the line of supporting copy some pages want underneath.
 *
 * DriverHome's pattern is structurally different (page-specific title, different
 * icon) and is intentionally excluded from this component. A separate
 * <PageMasthead> can be added in a future slice if desired.
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TypographyProps } from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import BrandLogo from './BrandLogo';

export interface BrandMastheadProps {
  /** Logo height in px. Defaults to 56. */
  logoHeight?: number;
  /**
   * Surface the masthead sits on — forwarded to <BrandLogo>. Defaults to
   * `'auto'` (follows the active color mode).
   */
  on?: 'light' | 'dark' | 'auto';
  /** Optional supporting line rendered under the logo. */
  tagline?: React.ReactNode;
  /** MUI Typography variant for the (visually hidden) brand heading. */
  titleVariant?: TypographyProps['variant'];
  /** Semantic HTML element for the heading. Defaults to 'h1'. */
  titleComponent?: React.ElementType;
  /** Outer Box sx. */
  sx?: SxProps<Theme>;
}

export default function BrandMasthead({
  logoHeight = 56,
  on = 'auto',
  tagline,
  titleVariant = 'h4',
  titleComponent = 'h1',
  sx,
}: BrandMastheadProps) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        ...sx,
      }}
    >
      {/* Decorative: the visually-hidden heading below already names the brand. */}
      <BrandLogo on={on} height={logoHeight} decorative />

      <Typography variant={titleVariant} component={titleComponent} sx={visuallyHidden}>
        {t('app.brand')}
      </Typography>

      {tagline !== undefined && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {tagline}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Clip the heading out of the visual layout while leaving it in the a11y tree.
 * Local copy rather than MUI's `visuallyHidden` util so the masthead has no
 * dependency on @mui/utils internals.
 */
const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;
